import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Spin, Table, Tag, message } from 'antd';
import { CheckOutlined, EditOutlined, SyncOutlined } from '@ant-design/icons';
import { adminContactSubmissionService } from '../../services/adminService';
import './ServiceReservations.css';

interface ContactSubmission {
  id: string;
  full_name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  admin_notes?: string;
  created_at: string;
}

const ContactSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [editingSubmission, setEditingSubmission] = useState<ContactSubmission | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminContactSubmissionService.getAll(100, 0, statusFilter, searchText || undefined);
      const rows = Array.isArray(response.data) ? (response.data as ContactSubmission[]) : [];
      setSubmissions(rows);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch contact submissions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchText]);

  useEffect(() => {
    void fetchSubmissions();
  }, [fetchSubmissions]);

  const openEdit = (submission: ContactSubmission) => {
    setEditingSubmission(submission);
    form.setFieldsValue({
      name: submission.full_name,
      email: submission.email,
      subject: submission.subject,
      message: submission.message,
      status: submission.status,
      adminNotes: submission.admin_notes,
    });
    setEditVisible(true);
  };

  const submitEdit = async (values: any) => {
    if (!editingSubmission) return;
    try {
      setLoading(true);
      await adminContactSubmissionService.update(editingSubmission.id, values);
      message.success('Contact submission updated');
      setEditVisible(false);
      fetchSubmissions();
    } catch (error) {
      console.error(error);
      message.error('Failed to update contact submission');
    } finally {
      setLoading(false);
    }
  };

  const markInProgress = async (id: string) => {
    try {
      setLoading(true);
      await adminContactSubmissionService.markInProgress(id);
      message.success('Submission marked in progress');
      fetchSubmissions();
    } catch (error) {
      console.error(error);
      message.error('Failed to update submission status');
    } finally {
      setLoading(false);
    }
  };

  const resolveSubmission = async (id: string) => {
    try {
      setLoading(true);
      await adminContactSubmissionService.resolve(id);
      message.success('Submission resolved');
      fetchSubmissions();
    } catch (error) {
      console.error(error);
      message.error('Failed to resolve submission');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      new: 'orange',
      in_progress: 'blue',
      resolved: 'green',
      closed: 'default',
    };
    return map[status] || 'default';
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 160,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: 220,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 300,
      render: (value: string) => (value.length > 90 ? `${value.slice(0, 90)}...` : value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value: string) => (
        <Tag color={getStatusColor(value)} style={{ textTransform: 'capitalize' }}>
          {value.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Received',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value: string) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: ContactSubmission) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          {record.status === 'new' ? (
            <Button icon={<SyncOutlined />} onClick={() => markInProgress(record.id)} />
          ) : null}
          {record.status !== 'resolved' && record.status !== 'closed' ? (
            <Button icon={<CheckOutlined />} type="primary" onClick={() => resolveSubmission(record.id)} />
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div className="service-reservations-container">
      <div className="service-reservations-header">
        <h1>Contact Form Submissions</h1>
        <p>Manage messages submitted through the website contact form.</p>
      </div>

      <div className="service-reservations-filters">
        <Input
          placeholder="Search by name, email, subject or message"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 360 }}
          allowClear
        />
        <Select
          placeholder="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          allowClear
          style={{ width: 220 }}
          options={[
            { label: 'New', value: 'new' },
            { label: 'In Progress', value: 'in_progress' },
            { label: 'Resolved', value: 'resolved' },
            { label: 'Closed', value: 'closed' },
          ]}
        />
        <Button onClick={fetchSubmissions} loading={loading}>Refresh</Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={submissions}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1300 }}
        />
      </Spin>

      <Modal
        title="Edit Contact Submission"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={submitEdit}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="adminNotes" label="Admin Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'New', value: 'new' },
                { label: 'In Progress', value: 'in_progress' },
                { label: 'Resolved', value: 'resolved' },
                { label: 'Closed', value: 'closed' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContactSubmissions;
